import { createPublicClient, createWalletClient, custom, decodeEventLog, getAddress, hexToString, http, isAddress, keccak256, parseAbi, stringToHex, toHex, type Address, type EIP1193Provider, type Hash } from 'viem';
import { bsc, bscTestnet } from 'viem/chains';
import type { CdtBenefitApiClaimsPage, CdtBenefitApiCountryReport, CdtBenefitApiImportsPage, CdtBenefitApiRatesPage, CdtBenefitApiRegisterPage, CdtBenefitApiView, CdtBenefitClaimsPage, CdtBenefitCountryReport, CdtBenefitImportsPage, CdtBenefitMemberView, CdtBenefitParams, CdtBenefitRatesView, CdtBenefitRegisterPage, CdtBenefitStatus, CdtBridgeApiHealth, CdtBridgeHealth, CdtSettlementStatus } from './lookup-types';
import type { CdttConfiguration, CdttStep } from './cdtt';
import { api } from './api';

/*
 * docs/CDT_BENEFIT_SPEC.md (rule 43) - the independent CDT Benefit Model. Every figure the web shows comes from the
 * BDynamicCdtBenefit contract (live reads / indexed events through the API); this module holds the formatting helpers
 * (bytes32 <-> country code, CDT atomic, local minor units, days left) and the member-side transactions:
 *   selectCountry(id, country)                               - from the ID's wallet, any time before a schedule exists
 *   activate(id)                                             - opens the locked schedule once the currency is configured
 *   claim(id)                                                - mints the claimable CDT (Option B only) to the schedule wallet
 *   Option A: the native BTCB liquidation (lib/cdtt.ts liquidatePrimaryToken); settleOptionA(id, redemptionId) records it
 *             (a server keeper sends it automatically; the admin test helper may too) and voids the locked CDT.
 *   Option B: coordinator.requestRedemption(CDT_BENEFIT, tokenId) then benefit.convert(redemptionId) - two steps, same
 *             shape as the liquidation.
 * v2 (owner settlement workflow 2026-10-21): CDT accrues LOCKED from the join as soon as the schedule opens at activation
 * (country selection); at maturity the member settles ONCE (settlementStatus 0 -> 1 SETTLED_LIQUIDITY | 2 SETTLED_CDTT).
 * Bridge (2026-10-22, B-Dynamic <-> CDT Platform): claimTo(id, to) mints the claimable CDT either to the ID's own wallet or
 * straight into the CDT Platform pool (cdtPlatformPool(), owner-set) - "Import to CDT Platform"; importedCdt(id) and the
 * BenefitImported events keep the imported total per ID. Only settled Option B IDs can claim / import.
 */

/** keccak256("CDT_BENEFIT") - the provider id registered in BDynamicProviderRegistry (spec s.1). */
export const CDT_BENEFIT_PROVIDER_ID: Hash = keccak256(toHex('CDT_BENEFIT'));

export const CDT_BENEFIT_MEMBER_ABI = parseAbi([
  'function selectCountry(uint256 id, bytes32 country)',
  'function activate(uint256 id)',
  'function claim(uint256 id)',
  'function claimTo(uint256 id, address to)',
  'function convert(uint256 redemptionId)',
  'function settleOptionA(uint256 id, uint256 redemptionId)',
  'function claimable(uint256 id) view returns (uint256)',
  'function cdtPlatformPool() view returns (address)',
  'function importedCdt(uint256 id) view returns (uint256)',
  'function lockedBalance(uint256 id) view returns (uint256)',
  'function settlementStatus(uint256 id) view returns (uint8)',
  'function countryOf(uint256 id) view returns (bytes32)',
]);

/**
 * Lazy oracle (contract feature 2026-10-31): anyone may push the API-signed rate bundle on-chain once the window (6 h) has
 * elapsed - the next member to register pays that one-time gas. GET /api/v1/cdt/rates-bundle serves the bundle + due flag.
 * Client decision 2026-11-01: on the Join page this is NOT a separate click - the country transaction is
 * selectCountryWithRates(id, country, <bundle>) when an update is due; the contract applies the bundle best effort
 * (RateBundleSkipped / RateSkippedOutOfBounds instead of reverts) and then selects the country. submitSignedRates stays
 * for the admin "Update rates now" button.
 */
export const CDT_BENEFIT_ORACLE_ABI = parseAbi([
  'function submitSignedRates(bytes32[] currencies, uint256[] minorUnits, uint256[] cdtPerMinorUnits, uint64 issuedAt, bytes signature)',
  'function selectCountryWithRates(uint256 id, bytes32 country, bytes32[] currencies, uint256[] minorUnits, uint256[] cdtPerMinorUnits, uint64 issuedAt, bytes signature)',
  'function oracleUpdateDue() view returns (bool due, uint64 nextAllowedAt)',
  'function lastOracleUpdate() view returns (uint64)',
  'function oracleWindowSeconds() view returns (uint64)',
  'event SignedRatesApplied(address indexed submitter, uint256 count, uint64 issuedAt)',
  'event RateBundleSkipped(address indexed submitter, bytes32 reason)',
  'event RateSkippedOutOfBounds(bytes32 currency, uint256 previous, uint256 proposed)',
  'error OracleWindowNotElapsed(uint64 nextAllowedAt)',
  'error StaleRateBundle(uint64 issuedAt)',
  'error NotRateReporter(address signer)',
  'error InvalidParameters()',
  'error RateOutOfBounds(bytes32 currency, uint256 previous, uint256 proposed)',
]);

/** The contract skips a bundle whose issuedAt is further than 900 s from the block time; the web refetches beyond this age. */
export const RATES_BUNDLE_REUSE_SECONDS = 600;

/** Whether a loaded bundle may be carried by the country transaction: due, non-empty and still fresh enough to be accepted. */
export function ratesBundleUsable(bundle: CdtRatesBundle | null | undefined, nowMs: number = Date.now()): bundle is CdtRatesBundle {
  return Boolean(bundle && bundle.due && bundle.currencies.length > 0 && Math.abs(Math.floor(nowMs / 1000) - bundle.issuedAt) <= RATES_BUNDLE_REUSE_SECONDS);
}

/** GET /api/v1/cdt/rates-bundle (uint256 values as decimal strings; unix seconds for the times). */
export interface CdtRatesBundle {
  chainId: number | null;
  contract: string | null;
  currencies: Hash[];
  minorUnits: string[];
  cdtPerMinorUnits: string[];
  issuedAt: number;
  signature: Hash;
  due: boolean;
  nextAllowedAt: number;
  window: number;
}

/** The raw bundle body -> CdtRatesBundle; null when the shape is not usable (route absent, arrays mismatched, bad hex). */
export function normalizeRatesBundle(raw: unknown): CdtRatesBundle | null {
  if (!raw || typeof raw !== 'object') return null;
  const body = raw as Record<string, unknown>;
  const currencies = Array.isArray(body.currencies) ? body.currencies : null;
  const minorUnits = Array.isArray(body.minorUnits) ? body.minorUnits : null;
  const perMinor = Array.isArray(body.cdtPerMinorUnits) ? body.cdtPerMinorUnits : null;
  if (!currencies || !minorUnits || !perMinor || currencies.length !== minorUnits.length || currencies.length !== perMinor.length) return null;
  if (!currencies.every((value) => typeof value === 'string' && BYTES32_RE.test(value))) return null;
  const uint = (value: unknown): string | null => (typeof value === 'string' && /^\d+$/.test(value) ? value : typeof value === 'number' && Number.isInteger(value) && value >= 0 ? String(value) : typeof value === 'bigint' ? value.toString() : null);
  const minor = minorUnits.map(uint); const rates = perMinor.map(uint);
  if (minor.some((value) => value === null) || rates.some((value) => value === null)) return null;
  const signature = typeof body.signature === 'string' && /^0x[0-9a-fA-F]*$/.test(body.signature) ? (body.signature as Hash) : null;
  if (!signature) return null;
  const num = (value: unknown, fallback = 0): number => (typeof value === 'number' && Number.isFinite(value) ? value : typeof value === 'string' && /^\d+$/.test(value) ? Number(value) : fallback);
  return {
    chainId: typeof body.chainId === 'number' ? body.chainId : null,
    contract: typeof body.contract === 'string' ? body.contract : null,
    currencies: currencies as Hash[],
    minorUnits: minor as string[],
    cdtPerMinorUnits: rates as string[],
    issuedAt: num(body.issuedAt),
    signature,
    due: body.due === true,
    nextAllowedAt: num(body.nextAllowedAt),
    window: num(body.window),
  };
}

/** Error names of the CDT Benefit / CDT token contracts -> member-facing text (the raw wallet text is appended by the caller when useful). */
export const CDT_BENEFIT_REVERT_MESSAGES: Record<string, string> = {
  NotSettledCdtt: 'This ID has not settled Option B (CDT) yet, so nothing can be claimed or imported.',
  RateStale: 'The exchange rate of this currency is older than the allowed age; it must be refreshed before activation.',
  CrossBorderTransferBlocked: 'CDT can be transferred only between wallets of the same country. The destination wallet belongs to another country, so the contract refused the transfer.',
  CounterpartyWithoutCountry: 'The other wallet has no country recorded on the CDT Benefit contract, so the contract refused the transfer.',
  OracleWindowNotElapsed: 'The exchange rates were refreshed less than 6 hours ago; no update is needed right now.',
  StaleRateBundle: 'The signed rate bundle is too old. Reload the page to fetch a fresh one.',
  NotRateReporter: 'The rate bundle was not signed by the authorised rate reporter.',
  InvalidParameters: 'The rate bundle is malformed (empty or mismatched arrays).',
  RateOutOfBounds: 'A rate in the bundle moves more than the allowed deviation per refresh; governance must review it.',
};

/** The contract error name inside a viem / wallet error (custom error name, or the first known name in the message text), or null. */
export function cdtBenefitErrorName(cause: unknown): string | null {
  const seen = new Set<unknown>();
  let current: unknown = cause;
  while (current && typeof current === 'object' && !seen.has(current)) {
    seen.add(current);
    const record = current as { data?: { errorName?: unknown }; errorName?: unknown; cause?: unknown; message?: unknown; shortMessage?: unknown };
    const name = record.data && typeof record.data === 'object' && typeof record.data.errorName === 'string' ? record.data.errorName : typeof record.errorName === 'string' ? record.errorName : null;
    if (name && CDT_BENEFIT_REVERT_MESSAGES[name]) return name;
    for (const text of [record.shortMessage, record.message]) {
      if (typeof text !== 'string') continue;
      const match = Object.keys(CDT_BENEFIT_REVERT_MESSAGES).find((key) => new RegExp(`\\b${key}\\b`).test(text));
      if (match) return match;
    }
    current = record.cause;
  }
  return null;
}

/** Friendly text for a failed CDT Benefit / CDT token transaction: the mapped revert reason, else the error's own message, else `fallback`. */
export function describeCdtBenefitError(cause: unknown, fallback = 'The transaction failed.'): string {
  const name = cdtBenefitErrorName(cause);
  if (name) return CDT_BENEFIT_REVERT_MESSAGES[name]!;
  if (cause && typeof cause === 'object' && typeof (cause as { shortMessage?: unknown }).shortMessage === 'string') return (cause as { shortMessage: string }).shortMessage;
  return cause instanceof Error && cause.message ? cause.message : fallback;
}

const COORDINATOR_ABI = parseAbi([
  'function requestRedemption(bytes32 providerId, uint256 tokenId) returns (uint256)',
  'event RedemptionRequested(uint256 indexed redemptionId, uint256 indexed tokenId, bytes32 indexed providerId, uint64 providerVersion, address requester, uint256 settlementAmount, uint64 expiresAt)',
]);

const BYTES32_RE = /^0x[0-9a-fA-F]{64}$/;

/** ISO-3166 alpha-2 (or a currency code) -> bytes32, right-padded with zeros; "IN" -> 0x494e00…00. */
export function codeToBytes32(code: string): Hash {
  const clean = code.trim().toUpperCase();
  if (!/^[A-Z0-9]{1,32}$/.test(clean)) throw new Error(`"${code}" is not a valid code (letters / digits only, up to 32).`);
  return stringToHex(clean, { size: 32 });
}

/** bytes32 -> code ("0x494e00…" -> "IN"); 0x0 -> "". */
export function bytes32ToCode(value: string): string {
  if (!BYTES32_RE.test(value)) return value;
  if (/^0x0+$/.test(value)) return '';
  try { return hexToString(value as Hash, { size: 32 }).replace(/\0+$/, ''); } catch { return ''; }
}

/** The API may send a country / currency as text ("IN") or as the raw bytes32 - either way this gives the code, "" for none. */
export function normalizeCode(value: string | null | undefined): string {
  if (!value) return '';
  const trimmed = value.trim();
  return BYTES32_RE.test(trimmed) ? bytes32ToCode(trimmed) : trimmed.toUpperCase();
}

/** 18-decimal CDT atomic -> "1,234.56 CDT" (two decimals, rounded down; never a float on the whole part). */
export function formatCdt(atomic: string | bigint | null | undefined, symbol = 'CDT'): string {
  const raw = typeof atomic === 'bigint' ? atomic.toString() : (atomic ?? '0');
  if (!/^\d+$/.test(raw)) return `0.00 ${symbol}`;
  const value = BigInt(raw);
  const whole = value / 10n ** 18n;
  const cents = (value % 10n ** 18n) / 10n ** 16n;
  return `${whole.toLocaleString('en-US')}.${cents.toString().padStart(2, '0')} ${symbol}`;
}

/** Currency minor units (paise / cents) -> "₹1,234.56" style text; unknown currency code -> "1,234.56 XXX". */
export function formatLocal(minor: string | bigint | null | undefined, currency: string | null | undefined): string {
  const raw = typeof minor === 'bigint' ? minor.toString() : (minor ?? '0');
  const code = normalizeCode(currency);
  if (!/^\d+$/.test(raw)) return code ? `0.00 ${code}` : '0.00';
  const value = BigInt(raw);
  const whole = value / 100n;
  const cents = value % 100n;
  const amount = `${whole.toLocaleString('en-US')}.${cents.toString().padStart(2, '0')}`;
  if (!code) return amount;
  if (/^[A-Z]{3}$/.test(code)) {
    try {
      const parts = new Intl.NumberFormat('en-US', { style: 'currency', currency: code, currencyDisplay: 'narrowSymbol' }).formatToParts(0);
      const symbol = parts.find((part) => part.type === 'currency')?.value;
      if (symbol && symbol !== code) return `${symbol}${amount}`;
    } catch { /* not an ISO-4217 code known to the runtime */ }
  }
  return `${amount} ${code}`;
}

/** CDT credited per 1 MAJOR unit of the currency (1 INR, 1 USD) from the on-chain 18-decimal CDT-per-MINOR-unit value. */
export function cdtPerMajorUnit(cdtPerMinorAtomic: string | bigint | null | undefined): number {
  const raw = typeof cdtPerMinorAtomic === 'bigint' ? cdtPerMinorAtomic.toString() : (cdtPerMinorAtomic ?? '0');
  if (!/^\d+$/.test(raw) || raw === '0') return 0;
  return Number(BigInt(raw) * 100n) / 1e18;
}

/** Adaptive decimals for a CDT-per-unit figure: >= 100 -> 2, >= 1 -> 4, below 1 -> up to 6 (trimmed). */
export function formatCdtPerUnit(value: number): string {
  if (!Number.isFinite(value) || value <= 0) return '—';
  const digits = value >= 100 ? 2 : value >= 1 ? 4 : 6;
  return `${value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: digits })} CDT`;
}

/** Local currency per 1 CDT in minor units (paise / cents), rounded, from the CDT-per-minor-unit atomic value; null when unset. */
export function localMinorPerCdt(cdtPerMinorAtomic: string | bigint | null | undefined): bigint | null {
  const raw = typeof cdtPerMinorAtomic === 'bigint' ? cdtPerMinorAtomic.toString() : (cdtPerMinorAtomic ?? '0');
  if (!/^\d+$/.test(raw) || raw === '0') return null;
  const atomic = BigInt(raw);
  return (10n ** 18n + atomic / 2n) / atomic;
}

/** Human conversion line, e.g. "1 INR = 1 CDT · 1 CDT = ₹1.00" (owner rule 2026-10-31: 1 CDT = 1 unit of the local currency). */
export function formatRate(cdtPerMinorAtomic: string | bigint | null | undefined, currency: string | null | undefined): string {
  const code = normalizeCode(currency);
  const perMajor = cdtPerMajorUnit(cdtPerMinorAtomic);
  if (!perMajor) return 'not set';
  const minor = localMinorPerCdt(cdtPerMinorAtomic);
  return `1 ${code || 'unit'} = ${formatCdtPerUnit(perMajor)} · 1 CDT = ${minor !== null ? formatLocal(minor, code) : '—'}`;
}

/** Whole days left until `end` (ISO or unix seconds); 0 once passed, null when unknown. */
export function daysLeft(end: string | number | null | undefined, now: number = Date.now()): number | null {
  if (end === null || end === undefined || end === '') return null;
  const ms = typeof end === 'number' ? end * 1000 : /^\d+$/.test(end) ? Number(end) * 1000 : new Date(end).getTime();
  if (!Number.isFinite(ms)) return null;
  return Math.max(0, Math.ceil((ms - now) / 86_400_000));
}

/** Reference value in minor units for an issue era: Standard as configured, Half >> 1, Quarter >> 2 (spec s.1). */
export function referenceForEra(standardMinor: string, era: 'STANDARD' | 'HALF' | 'QUARTER' | number): bigint {
  const shift = typeof era === 'number' ? BigInt(era) : era === 'HALF' ? 1n : era === 'QUARTER' ? 2n : 0n;
  return /^\d+$/.test(standardMinor) ? BigInt(standardMinor) >> shift : 0n;
}

/** targetCdt = referenceMinor × cdtPerMinorUnit × multiplierBps / 10000 (the contract's formula, for quotes shown before signing). */
export function targetCdtAtomic(referenceMinor: bigint, cdtPerMinorUnit: string, multiplierBps: number): bigint {
  if (!/^\d+$/.test(cdtPerMinorUnit)) return 0n;
  return (referenceMinor * BigInt(cdtPerMinorUnit) * BigInt(Math.max(0, Math.round(multiplierBps)))) / 10_000n;
}

/** The configured row for a country (code in either text or bytes32 form). */
export function countryRow(params: CdtBenefitParams | null | undefined, country: string | null | undefined): CdtBenefitParams['countries'][number] | null {
  const code = normalizeCode(country);
  if (!code || !params?.countries) return null;
  return params.countries.find((row) => normalizeCode(row.country) === code) ?? null;
}

/** v2 settlementStatus(id) of a normalized view: 0 none (accruing locked), 1 Option A (liquidity, CDT voided), 2 Option B (CDT claimable), 3 forfeited (token burned unsettled, CDT voided). */
export function settlementOf(view: CdtBenefitMemberView | null | undefined): CdtSettlementStatus {
  if (!view) return 0;
  if (view.settlement === 1 || view.settlement === 2 || view.settlement === 3) return view.settlement;
  if (view.status === 'forfeited') return 3;
  if (view.election === 'B') return 2;
  if (view.election === 'A' || view.liquidatedAt) return 1;
  return 0;
}

/** The election a member / Lookup / admin register shows; 'A' = settled liquidity (or forfeited), 'B' = settled CDT, 'none' = not settled yet. */
export function electionOf(view: CdtBenefitMemberView | null | undefined): 'none' | 'A' | 'B' {
  const settlement = settlementOf(view);
  return settlement === 2 ? 'B' : settlement === 1 || settlement === 3 ? 'A' : 'none';
}

/** The v2 status word of a normalized view (the API's when it sends one, derived otherwise). */
export function statusOf(view: CdtBenefitMemberView | null | undefined): CdtBenefitStatus {
  if (!view || view.configured === false) return 'not_configured';
  const settlement = settlementOf(view);
  if (settlement === 3) return 'forfeited';
  if (settlement === 1) return 'settled_liquidity';
  if (settlement === 2) return view.schedule && /^\d+$/.test(view.schedule.remaining) && BigInt(view.schedule.remaining) === 0n ? 'completed' : 'settled_cdtt';
  return normalizeCode(view.country) ? 'accruing_locked' : 'country_missing';
}

/** Maturity = the Primary Token unfreeze (365 d; 600 s on the testnet): the API's maturityAt, else the token's unfreezeAt. */
export function maturityOf(view: CdtBenefitMemberView | null | undefined, tokenUnfreezeAt?: string | null): string | null {
  return view?.maturityAt ?? tokenUnfreezeAt ?? null;
}

/** Whole days from `start` to `at` (schedule day number); 0 before the start, null when unknown. */
export function dayOf(start: string | number | null | undefined, at: number = Date.now()): number | null {
  if (start === null || start === undefined || start === '') return null;
  const ms = typeof start === 'number' ? start * 1000 : /^\d+$/.test(start) ? Number(start) * 1000 : new Date(start).getTime();
  if (!Number.isFinite(ms)) return null;
  return Math.max(0, Math.floor((at - ms) / 86_400_000));
}

export function memberIdToUint(bdId: string): bigint {
  return BigInt(bdId.trim().replace(/^BD-/i, ''));
}

function chainFor(chainId: 56 | 97) { return chainId === 56 ? bsc : bscTestnet; }

function walletClientFor(chainId: 56 | 97, account: Address) {
  if (!window.ethereum) throw new Error('No injected EVM wallet is available.');
  return createWalletClient({ account, chain: chainFor(chainId), transport: custom(window.ethereum as unknown as EIP1193Provider) });
}

function benefitAddress(address: string | null | undefined): Address {
  if (!address || !isAddress(address)) throw new Error('The CDT Benefit contract address is not known yet (live parameters not loaded).');
  return getAddress(address);
}

/** selectCountry(id, country) from the connected wallet (must be the ID's wallet). */
export async function sendSelectCountry(chainId: 56 | 97, account: Address, contract: string | null | undefined, bdId: string | bigint, country: string): Promise<Hash> {
  const id = typeof bdId === 'bigint' ? bdId : memberIdToUint(bdId);
  const client = walletClientFor(chainId, account);
  return client.writeContract({ address: benefitAddress(contract), abi: CDT_BENEFIT_MEMBER_ABI, functionName: 'selectCountry', args: [id, codeToBytes32(country)] });
}

/** activate(id) - anyone may send it: opens the locked schedule of an ID whose currency was configured after its country selection. */
export async function sendActivate(chainId: 56 | 97, account: Address, contract: string | null | undefined, bdId: string | bigint): Promise<Hash> {
  const id = typeof bdId === 'bigint' ? bdId : memberIdToUint(bdId);
  const client = walletClientFor(chainId, account);
  return client.writeContract({ address: benefitAddress(contract), abi: CDT_BENEFIT_MEMBER_ABI, functionName: 'activate', args: [id] });
}

/** settleOptionA(id, redemptionId) - anyone may send it once the ID's native liquidation is CONFIRMED; voids the locked CDT. */
export async function sendSettleOptionA(chainId: 56 | 97, account: Address, contract: string | null | undefined, bdId: string | bigint, redemptionId: bigint): Promise<Hash> {
  const id = typeof bdId === 'bigint' ? bdId : memberIdToUint(bdId);
  const client = walletClientFor(chainId, account);
  return client.writeContract({ address: benefitAddress(contract), abi: CDT_BENEFIT_MEMBER_ABI, functionName: 'settleOptionA', args: [id, redemptionId] });
}

/** claim(id) - anyone may send it; the CDT goes to the schedule wallet (Option B only). */
export async function sendClaim(chainId: 56 | 97, account: Address, contract: string | null | undefined, bdId: string | bigint): Promise<Hash> {
  const id = typeof bdId === 'bigint' ? bdId : memberIdToUint(bdId);
  const client = walletClientFor(chainId, account);
  return client.writeContract({ address: benefitAddress(contract), abi: CDT_BENEFIT_MEMBER_ABI, functionName: 'claim', args: [id] });
}

/** claimTo(id, to) - the ID's wallet mints its claimable CDT to `to`: its own wallet, or the CDT Platform pool ("Import to CDT Platform"; member-paid gas, Option B only). */
export async function sendClaimTo(chainId: 56 | 97, account: Address, contract: string | null | undefined, bdId: string | bigint, to: string): Promise<Hash> {
  const id = typeof bdId === 'bigint' ? bdId : memberIdToUint(bdId);
  if (!isAddress(to) || /^0x0{40}$/i.test(to)) throw new Error('The destination of the import is not a valid address.');
  const client = walletClientFor(chainId, account);
  return client.writeContract({ address: benefitAddress(contract), abi: CDT_BENEFIT_MEMBER_ABI, functionName: 'claimTo', args: [id, getAddress(to)] });
}

/**
 * submitSignedRates(currencies, minorUnits, cdtPerMinorUnits, issuedAt, signature) from ANY connected wallet - the lazy
 * oracle push (one-time gas paid by the sender). The bundle comes from GET /api/v1/cdt/rates-bundle.
 */
export async function sendSubmitSignedRates(chainId: 56 | 97, account: Address, contract: string | null | undefined, bundle: CdtRatesBundle): Promise<Hash> {
  const target = checkedBundleTarget(chainId, contract, bundle);
  const client = walletClientFor(chainId, account);
  return client.writeContract({
    address: target, abi: CDT_BENEFIT_ORACLE_ABI, functionName: 'submitSignedRates',
    args: [bundle.currencies, bundle.minorUnits.map((value) => BigInt(value)), bundle.cdtPerMinorUnits.map((value) => BigInt(value)), BigInt(bundle.issuedAt), bundle.signature],
  });
}

/**
 * selectCountryWithRates(id, country, currencies, minorUnits, cdtPerMinorUnits, issuedAt, signature) from the ID's wallet
 * (client decision 2026-11-01): the registration's country transaction carries the signed sparse bundle; the contract
 * applies it best effort and then records the country exactly like selectCountry. One wallet confirmation, no extra step.
 */
export async function sendSelectCountryWithRates(chainId: 56 | 97, account: Address, contract: string | null | undefined, bdId: string | bigint, country: string, bundle: CdtRatesBundle): Promise<Hash> {
  const id = typeof bdId === 'bigint' ? bdId : memberIdToUint(bdId);
  const target = checkedBundleTarget(chainId, contract, bundle);
  const client = walletClientFor(chainId, account);
  return client.writeContract({
    address: target, abi: CDT_BENEFIT_ORACLE_ABI, functionName: 'selectCountryWithRates',
    args: [id, codeToBytes32(country), bundle.currencies, bundle.minorUnits.map((value) => BigInt(value)), bundle.cdtPerMinorUnits.map((value) => BigInt(value)), BigInt(bundle.issuedAt), bundle.signature],
  });
}

/** The contract a bundle may be sent to: non-empty, signed for this chain and (when it says so) for this very contract. */
function checkedBundleTarget(chainId: 56 | 97, contract: string | null | undefined, bundle: CdtRatesBundle): Address {
  if (!bundle.currencies.length) throw new Error('The rate bundle is empty.');
  if (bundle.chainId !== null && bundle.chainId !== chainId) throw new Error(`The rate bundle was signed for chain ${bundle.chainId}, not ${chainId}.`);
  const target = benefitAddress(contract);
  if (bundle.contract && isAddress(bundle.contract) && getAddress(bundle.contract) !== target) throw new Error('The rate bundle was signed for a different CDT Benefit contract.');
  return target;
}

function publicRpcUrl(chainId: 56 | 97): string {
  return import.meta.env.VITE_RPC_PUBLIC_URL?.trim() || (chainId === 56 ? 'https://bsc-dataseed.bnbchain.org' : 'https://data-seed-prebsc-1-s1.bnbchain.org:8545');
}

/** Waits for a CDT Benefit transaction over the public RPC; resolves true when it succeeded, false when it reverted. */
export async function waitForCdtBenefitReceipt(chainId: 56 | 97, hash: Hash): Promise<boolean> {
  const client = createPublicClient({ chain: chainFor(chainId), transport: http(publicRpcUrl(chainId), { timeout: 10_000, retryCount: 2 }) });
  const receipt = await client.waitForTransactionReceipt({ hash });
  return receipt.status === 'success';
}

/**
 * oracleUpdateDue() live - the signed bundle's `due` flag is the API's view; this is the contract's. null when the live
 * contract does not have the lazy oracle yet (the call reverts / the ABI is unknown) - the UI then treats it as "not due".
 */
export async function readOracleUpdateDue(chainId: 56 | 97, contract: string | null | undefined): Promise<{ due: boolean; nextAllowedAt: number } | null> {
  try {
    const client = createPublicClient({ chain: chainFor(chainId), transport: http(publicRpcUrl(chainId), { timeout: 10_000, retryCount: 1 }) });
    const [due, nextAllowedAt] = await client.readContract({ address: benefitAddress(contract), abi: CDT_BENEFIT_ORACLE_ABI, functionName: 'oracleUpdateDue' });
    return { due, nextAllowedAt: Number(nextAllowedAt) };
  } catch {
    return null;
  }
}

/**
 * GET /api/v1/cdt/rates-bundle -> CdtRatesBundle; null when the route is absent / fails / returns an unusable body (= skip
 * silently, the registration never depends on the oracle). `for: 'join'` = the sparse bundle the country transaction
 * carries (cap 40: the member's `currency` first, USD, drifted values, oldest stamps); no options = the full bundle of the
 * admin button.
 */
export async function loadRatesBundle(options: { for?: 'join' | 'admin'; currency?: string | null } = {}): Promise<CdtRatesBundle | null> {
  const params = new URLSearchParams();
  if (options.for) params.set('for', options.for);
  const currency = normalizeCode(options.currency);
  if (/^[A-Z]{3}$/.test(currency)) params.set('currency', currency);
  const query = params.toString();
  try { return normalizeRatesBundle(await api<unknown>(`/api/v1/cdt/rates-bundle${query ? `?${query}` : ''}`)); } catch { return null; }
}

/** A checksummed address, or null for an absent / malformed / zero address (the contract's "unset" value). */
export function nonZeroAddress(value: string | null | undefined): Address | null {
  if (!value || !isAddress(value) || /^0x0{40}$/i.test(value)) return null;
  return getAddress(value);
}

/** The CDT Platform pool an import would target: the ID's block first (API per-ID field), then the live params; null = import hidden. */
export function platformPoolOf(params: CdtBenefitParams | null | undefined, view: CdtBenefitMemberView | null | undefined): Address | null {
  return nonZeroAddress(view?.platformPool) ?? nonZeroAddress(params?.platformPool);
}

export interface CdtBenefitElectionResult { redemptionId: bigint; requestHash: Hash; convertHash: Hash }

/**
 * Option B (spec s.1 "Provider function"): 1. coordinator.requestRedemption(keccak256("CDT_BENEFIT"), tokenId) - the
 * Primary Token goes into escrow; 2. benefit.convert(redemptionId) - settles the Token Liquidity to the CDT platform and
 * makes the locked CDT claimable (settlementStatus 2). Same two-step shape and progress callbacks as the liquidation.
 */
export async function electCdtBenefit(
  config: CdttConfiguration,
  benefitContract: string | null | undefined,
  account: Address,
  tokenId: bigint,
  onProgress: (step: CdttStep, hash?: Hash) => void,
): Promise<CdtBenefitElectionResult> {
  const benefit = benefitAddress(benefitContract);
  const chain = chainFor(config.chainId);
  const publicClient = createPublicClient({ chain, transport: http(config.rpcUrl, { timeout: 10_000, retryCount: 2 }) });
  const walletClient = walletClientFor(config.chainId, account);

  onProgress('requesting');
  const requestHash = await walletClient.writeContract({ address: config.coordinator, abi: COORDINATOR_ABI, functionName: 'requestRedemption', args: [CDT_BENEFIT_PROVIDER_ID, tokenId] });
  const requestReceipt = await publicClient.waitForTransactionReceipt({ hash: requestHash });
  if (requestReceipt.status !== 'success') throw new Error('The redemption request was reverted on-chain.');
  let redemptionId: bigint | null = null;
  for (const log of requestReceipt.logs) {
    try {
      const decoded = decodeEventLog({ abi: COORDINATOR_ABI, data: log.data, topics: log.topics });
      if (decoded.eventName === 'RedemptionRequested') { redemptionId = decoded.args.redemptionId; break; }
    } catch { /* not ours */ }
  }
  if (redemptionId === null) throw new Error('The redemption id could not be read from the receipt.');
  onProgress('escrowed', requestHash);

  onProgress('converting');
  const convertHash = await walletClient.writeContract({ address: benefit, abi: CDT_BENEFIT_MEMBER_ABI, functionName: 'convert', args: [redemptionId] });
  const convertReceipt = await publicClient.waitForTransactionReceipt({ hash: convertHash });
  if (convertReceipt.status !== 'success') throw new Error('The CDT Benefit election was reverted on-chain.');
  onProgress('done', convertHash);
  return { redemptionId, requestHash, convertHash };
}

/* ---- API boundary (coordinator 2026-10-20, API final): raw response -> the view types the components render. ---- */
const str = (value: unknown, fallback = '0'): string => (typeof value === 'string' && value !== '' ? value : typeof value === 'number' ? String(value) : fallback);
const isNormalizedView = (value: object): value is CdtBenefitMemberView => 'targetCdt' in value && !('targetCdtAtomic' in value);

/** The v2 settlement status from an API block: settlementStatus number first, then the status word, then the election. */
function settlementFromApi(api: CdtBenefitApiView): CdtSettlementStatus {
  if (api.settlementStatus === 1 || api.settlementStatus === 2 || api.settlementStatus === 3) return api.settlementStatus;
  if (api.settlementStatus === 0) return 0;
  if (api.status === 'forfeited') return 3;
  if (api.status === 'settled_liquidity' || api.status === 'option_a') return 1;
  if (api.status === 'settled_cdtt' || api.status === 'completed' || api.status === 'option_b') return 2;
  if (api.election === 'optionA') return 1;
  if (api.election === 'optionB') return 2;
  return 0;
}

/** `CdtBenefitView` (member / lookup / dashboard / public / me endpoints) -> CdtBenefitMemberView; already-normalized input passes through. */
export function normalizeMemberView(raw: CdtBenefitApiView | CdtBenefitMemberView | null | undefined): CdtBenefitMemberView | null {
  if (!raw || typeof raw !== 'object') return null;
  if (isNormalizedView(raw)) return raw;
  const api = raw as CdtBenefitApiView;
  if (api.configured === false || api.status === 'not_configured') return { configured: false, ...(api.reason ? { reason: api.reason } : {}), country: null, currency: null, referenceMinor: null, targetCdt: '0', dailyCdt: '0', scheduleDays: api.scheduleDays ?? 0, multiplierBps: api.multiplierBps ?? 0, ready: false, election: 'none', settlement: 0, status: 'not_configured', lockedBalance: '0', voidedCdt: '0', schedule: null };
  const schedule = api.schedule;
  const quote = api.quote;
  const settlement = settlementFromApi(api);
  const election: 'none' | 'A' | 'B' = settlement === 1 || settlement === 3 ? 'A' : settlement === 2 ? 'B' : 'none';
  const country = api.country ?? schedule?.country ?? null;
  const remaining = str(api.remainingAtomic);
  // v1 APIs have no lockedBalanceAtomic: before any settlement the live accrued figure IS the locked balance (lockedBalance(id) == accrued(id)).
  const lockedBalance = api.lockedBalanceAtomic !== undefined && api.lockedBalanceAtomic !== null ? str(api.lockedBalanceAtomic) : settlement === 0 ? str(api.accruedAtomic) : '0';
  const status: CdtBenefitStatus = api.status === 'accruing_locked' || api.status === 'country_missing' || api.status === 'settled_liquidity' || api.status === 'settled_cdtt' || api.status === 'completed' || api.status === 'forfeited'
    ? api.status
    : settlement === 3 ? 'forfeited' : settlement === 1 ? 'settled_liquidity' : settlement === 2 ? (schedule && /^\d+$/.test(remaining) && BigInt(remaining) === 0n ? 'completed' : 'settled_cdtt') : normalizeCode(country) ? 'accruing_locked' : 'country_missing';
  return {
    configured: true,
    ...(api.bdId ? { bdId: api.bdId } : {}),
    country,
    currency: api.currency ?? schedule?.currency ?? quote?.currency ?? null,
    countrySelectedAt: api.countrySelectedAt ?? null,
    countryByGovernance: api.countryByGovernance === true,
    // The quote of an ID without a country (or without a reference value) says "0": that is "unknown", not an amount.
    referenceMinor: api.referenceValueMinor ?? schedule?.referenceValueMinor ?? (quote?.referenceValueMinor && /^[1-9]\d*$/.test(quote.referenceValueMinor) ? quote.referenceValueMinor : null),
    targetCdt: str(api.targetCdtAtomic, str(schedule?.targetCdtAtomic, str(quote?.targetCdtAtomic))),
    dailyCdt: str(api.dailyCdtAtomic, str(quote?.dailyCdtAtomic)),
    scheduleDays: api.scheduleDays ?? quote?.scheduleDays ?? 0,
    multiplierBps: api.multiplierBps ?? 0,
    ready: quote?.ready ?? Boolean(schedule),
    election,
    settlement,
    status,
    lockedBalance,
    voidedCdt: str(api.voidedCdtAtomic),
    settledAt: api.settledAt ?? (settlement === 1 ? api.optionA?.at ?? null : settlement === 2 ? schedule?.at ?? null : null),
    settledTxHash: api.settledTxHash ?? (settlement === 1 ? api.optionA?.txHash ?? null : settlement === 2 ? schedule?.txHash ?? null : null),
    maturityAt: api.maturityAt ?? null,
    canSettle: typeof api.canSettle === 'boolean' ? api.canSettle : null,
    canChangeCountry: typeof api.canChangeCountry === 'boolean' ? api.canChangeCountry : null,
    liquidatedAt: api.optionA?.at ?? null,
    liquidationTxHash: api.optionA?.txHash ?? null,
    schedule: schedule ? {
      redemptionId: schedule.redemptionId, tokenId: schedule.tokenId ?? null, wallet: schedule.wallet ?? api.wallet ?? null, country: schedule.country, currency: schedule.currency,
      referenceMinor: str(schedule.referenceValueMinor), targetCdt: str(schedule.targetCdtAtomic), start: schedule.start, end: schedule.end,
      claimed: str(api.claimedAtomic), accrued: str(api.accruedAtomic), claimable: str(api.claimableAtomic), remaining,
      settledAtomic: str(schedule.settledAtomic), txHash: schedule.txHash ?? null, openedAt: schedule.at ?? null,
    } : null,
    claims: (api.claims ?? []).map((claim) => ({ amount: str(claim.amountAtomic), totalClaimed: str(claim.totalClaimedAtomic), remaining: str(claim.remainingAtomic), wallet: claim.wallet ?? null, txHash: claim.txHash, at: claim.at })),
    // Bridge fields (absent on an API without the bridge): imported total, BenefitImported rows, the pool for this ID.
    imported: str(api.importedAtomic),
    imports: (Array.isArray(api.imports) ? api.imports : []).map((row) => ({ amount: str(row.amountAtomic), totalImported: str(row.totalImportedAtomic), pool: row.pool ?? '', wallet: api.wallet ?? null, txHash: row.txHash, at: row.at })),
    platformPool: api.platformPool ?? null,
  };
}

/** Bridge admin GET /cdt-benefit/imports -> CdtBenefitImportsPage (byDay / byPool are top-level in the API, every block optional). */
export function normalizeImportsPage(raw: CdtBenefitApiImportsPage | null | undefined): CdtBenefitImportsPage | null {
  if (!raw || typeof raw !== 'object' || !Array.isArray(raw.items)) return null;
  return {
    items: raw.items.map((row) => ({ bdId: row.bdId, wallet: row.wallet ?? null, pool: row.pool ?? '', amount: str(row.amountAtomic), totalImported: str(row.totalImportedAtomic), txHash: row.txHash, at: row.at })),
    totalCount: raw.total ?? raw.items.length,
    totals: {
      amount: str(raw.totals?.amountAtomic), count: raw.totals?.imports ?? raw.items.length,
      byDay: (Array.isArray(raw.byDay) ? raw.byDay : []).map((row) => ({ day: row.day, amount: str(row.amountAtomic), count: row.imports })),
      byPool: (Array.isArray(raw.byPool) ? raw.byPool : []).map((row) => ({ pool: row.pool, amount: str(row.amountAtomic), count: row.imports })),
    },
  };
}

/** Bridge public GET /api/v1/cdt/health -> CdtBridgeHealth; null when the route is absent or the body is not an object. */
export function normalizeBridgeHealth(raw: CdtBridgeApiHealth | null | undefined): CdtBridgeHealth | null {
  if (!raw || typeof raw !== 'object') return null;
  return {
    enabled: raw.enabled === true,
    chainId: typeof raw.chainId === 'number' ? raw.chainId : null,
    cdtBenefitAddress: nonZeroAddress(raw.cdtBenefitAddress),
    platformPool: nonZeroAddress(raw.platformPool),
  };
}

/** Admin GET /cdt-benefit/register -> CdtBenefitRegisterPage. */
export function normalizeRegisterPage(raw: CdtBenefitApiRegisterPage | null | undefined): CdtBenefitRegisterPage | null {
  if (!raw || !Array.isArray(raw.items)) return null;
  return {
    items: raw.items.map((row) => ({
      bdId: row.bdId, wallet: row.wallet ?? null, country: row.country, currency: row.currency, referenceMinor: str(row.referenceValueMinor), targetCdt: str(row.targetCdtAtomic), start: row.start, end: row.end,
      accrued: str(row.accruedAtomic), claimed: str(row.claimedAtomic), remaining: str(row.remainingAtomic), claimable: str(row.claimableAtomic), status: row.status, redemptionId: row.redemptionId, tokenId: row.tokenId ?? null,
      settledAtomic: str(row.settledAtomic), txHash: row.txHash ?? null, at: row.at,
      voidedCdt: str(row.voidedCdtAtomic), settledAt: row.settledAt ?? null, settledTxHash: row.settledTxHash ?? null, lockedBalance: str(row.lockedBalanceAtomic),
    })),
    totalCount: raw.total ?? raw.items.length,
    ...(raw.totals ? { totals: { schedules: raw.totals.schedules, targetCdt: str(raw.totals.targetCdtAtomic), claimed: str(raw.totals.claimedAtomic), remaining: str(raw.totals.remainingAtomic), settledAtomic: str(raw.totals.settledAtomic), voidedCdt: str(raw.totals.voidedCdtAtomic) } } : {}),
  };
}

/** Admin GET /cdt-benefit/claims -> CdtBenefitClaimsPage (byDay / byCountry are top-level in the API). */
export function normalizeClaimsPage(raw: CdtBenefitApiClaimsPage | null | undefined): CdtBenefitClaimsPage | null {
  if (!raw || !Array.isArray(raw.items)) return null;
  return {
    items: raw.items.map((row) => ({ bdId: row.bdId, wallet: row.wallet ?? null, country: row.country ?? null, currency: row.currency ?? null, amount: str(row.amountAtomic), totalClaimed: str(row.totalClaimedAtomic), remaining: str(row.remainingAtomic), txHash: row.txHash, at: row.at })),
    totalCount: raw.total ?? raw.items.length,
    totals: {
      amount: str(raw.totals?.amountAtomic), count: raw.totals?.claims ?? raw.items.length,
      byDay: (raw.byDay ?? []).map((row) => ({ day: row.day, amount: str(row.amountAtomic), count: row.claims })),
      byCountry: (raw.byCountry ?? []).map((row) => ({ country: row.country, currency: row.currency ?? null, amount: str(row.amountAtomic), count: row.claims })),
    },
  };
}

/** Admin GET /cdt-benefit/countries -> CdtBenefitCountryReport (one row per country; settlement split A / B / accruing, v1 names accepted). */
export function normalizeCountryReport(raw: CdtBenefitApiCountryReport | null | undefined): CdtBenefitCountryReport | null {
  if (!raw || !Array.isArray(raw.perCountry)) return null;
  const without = Array.isArray(raw.withoutSelection) ? raw.withoutSelection : [];
  const num = (...values: Array<number | undefined>) => values.find((value) => typeof value === 'number') ?? 0;
  return {
    items: raw.perCountry.map((row) => ({
      country: row.country, currency: row.currency ?? null, ids: row.ids,
      withSchedule: num(row.settledCdtt, row.optionB), electionA: num(row.settledLiquidity, row.optionA), electionB: num(row.settledCdtt, row.optionB), none: num(row.accruing, row.none),
      ...(row.completed !== undefined ? { completed: row.completed } : {}), ...(row.byGovernance !== undefined ? { byGovernance: row.byGovernance } : {}),
    })),
    withoutCountry: raw.totals?.withoutSelection ?? without.length,
    withoutCountryIds: without,
    totalIds: raw.totals?.ids ?? raw.perCountry.reduce((sum, row) => sum + row.ids, 0) + without.length,
    ...(raw.totals ? { totals: { withSelection: raw.totals.withSelection, electionA: num(raw.totals.settledLiquidity, raw.totals.electionA), electionB: num(raw.totals.settledCdtt, raw.totals.electionB), electionNone: num(raw.totals.accruing, raw.totals.electionNone), ...(raw.totals.completed !== undefined ? { completed: raw.totals.completed } : {}) } } : {}),
  };
}

/**
 * v2 admin GET /cdt-benefit/rates -> CdtBenefitRatesView. The route is new (API engineer 2026-10-21); the row field names are
 * taken defensively (updatedAt | rateUpdatedAt, txHash | lastTxHash) and, when the route is absent, `ratesFromParams`
 * builds the same view from the live params (each country row carries rateUpdatedAt + stale).
 */
export function normalizeRatesPage(raw: CdtBenefitApiRatesPage | null | undefined, params?: CdtBenefitParams | null): CdtBenefitRatesView | null {
  const rows = raw && typeof raw === 'object' ? (Array.isArray(raw.items) ? raw.items : Array.isArray(raw.rates) ? raw.rates : null) : null;
  if (!rows) return null;
  return {
    items: rows.map((row) => {
      const currency = normalizeCode(row.currency);
      const configured = (params?.countries ?? []).filter((entry) => normalizeCode(entry.currency) === currency);
      return {
        currency,
        countries: Array.isArray(row.countries) && row.countries.length ? row.countries.map((code) => normalizeCode(code)) : configured.map((entry) => normalizeCode(entry.country)),
        referenceMinor: str(row.referenceValueMinor, str(configured[0]?.referenceValueMinor)),
        cdtPerMinorUnit: str(row.cdtPerMinorUnit, str(configured[0]?.cdtPerMinorUnit)),
        updatedAt: row.updatedAt ?? row.rateUpdatedAt ?? row.at ?? configured[0]?.rateUpdatedAt ?? null,
        stale: typeof row.stale === 'boolean' ? row.stale : typeof configured[0]?.stale === 'boolean' ? configured[0]!.stale! : null,
        txHash: row.txHash ?? row.lastTxHash ?? null,
      };
    }),
    reporter: raw!.reporter ?? raw!.rateReporter ?? params?.rateReporter ?? null,
    maxRateAgeSeconds: raw!.maxRateAgeSeconds ?? params?.maxRateAgeSeconds ?? null,
    readAt: raw!.readAt ?? null,
  };
}

/** The rates view from the live params alone (one row per currency) - used while the /cdt-benefit/rates route is absent. */
export function ratesFromParams(params: CdtBenefitParams | null | undefined): CdtBenefitRatesView | null {
  if (!params) return null;
  const byCurrency = new Map<string, CdtBenefitParams['countries']>();
  for (const row of params.countries ?? []) {
    const currency = normalizeCode(row.currency);
    if (!currency) continue;
    byCurrency.set(currency, [...(byCurrency.get(currency) ?? []), row]);
  }
  return {
    items: [...byCurrency.entries()].map(([currency, rows]) => ({
      currency, countries: rows.map((row) => normalizeCode(row.country)), referenceMinor: str(rows[0]!.referenceValueMinor), cdtPerMinorUnit: str(rows[0]!.cdtPerMinorUnit),
      updatedAt: rows[0]!.rateUpdatedAt ?? null, stale: typeof rows[0]!.stale === 'boolean' ? rows[0]!.stale! : null, txHash: null,
    })),
    reporter: params.rateReporter ?? null,
    maxRateAgeSeconds: params.maxRateAgeSeconds ?? null,
    readAt: params.readAt ?? null,
  };
}

/** Freshness of a keeper rate against maxRateAgeSeconds: 'fresh' | 'stale' | 'never' | 'unchecked' (window 0 / unknown). */
export function rateFreshness(updatedAt: string | null | undefined, maxRateAgeSeconds: number | null | undefined, stale?: boolean | null, now: number = Date.now()): 'fresh' | 'stale' | 'never' | 'unchecked' {
  if (!updatedAt) return 'never';
  if (typeof stale === 'boolean') return stale ? 'stale' : 'fresh';
  if (!maxRateAgeSeconds) return 'unchecked';
  const ms = /^\d+$/.test(updatedAt) ? Number(updatedAt) * 1000 : new Date(updatedAt).getTime();
  if (!Number.isFinite(ms)) return 'never';
  return now - ms > maxRateAgeSeconds * 1000 ? 'stale' : 'fresh';
}
